/**
 * Header component with navigation for Plan Guide.
 *
 * Phase 5: Enhanced with accessibility (ARIA) and responsive styling.
 *
 * @see frontend/workspace/CU-868gzzjua/frontend_research.md - Q7 Mobile Responsiveness
 */

import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { PAGE_IDS } from '../constants';

interface Props {
  title: string;
  currentPage: string;
  visitedPages: string[];
  canGoBack: boolean;
  onBack: () => void;
  onClose?: () => void;
}

// Define page order for progress calculation
const PAGE_ORDER = [
  PAGE_IDS.HUB,
  PAGE_IDS.GOALS,
  PAGE_IDS.ASSETS,
  PAGE_IDS.RECOMMENDATIONS,
  PAGE_IDS.FIX,
  PAGE_IDS.COMPARISON,
];

export const PlanGuideHeader: React.FC<Props> = ({
  title,
  currentPage,
  canGoBack,
  onBack,
  onClose,
}) => {
  // Calculate progress - cast to allow indexOf lookup
  const currentIndex = PAGE_ORDER.indexOf(currentPage as (typeof PAGE_ORDER)[number]);
  const progress =
    currentIndex >= 0 ? ((currentIndex + 1) / PAGE_ORDER.length) * 100 : 0;
  const progressLabel = `Step ${currentIndex + 1} of ${PAGE_ORDER.length}`;

  return (
    <AppBar
      component="header"
      position="static"
      color="default"
      elevation={1}
      role="banner"
    >
      <Toolbar
        sx={{
          // Responsive padding
          px: { xs: 1, sm: 2 },
          // Ensure minimum touch target height on mobile
          minHeight: { xs: 56, sm: 64 },
        }}
      >
        {canGoBack && (
          <Tooltip title="Go back to previous page">
            <IconButton
              edge="start"
              onClick={onBack}
              sx={{
                mr: 1,
                // Ensure 44x44px touch target
                minWidth: 44,
                minHeight: 44,
              }}
              aria-label="Go back to previous page"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}

        <Typography
          variant="h6"
          component="h1"
          sx={{
            flexGrow: 1,
            // Responsive font size
            fontSize: { xs: '1rem', sm: '1.25rem' },
            // Truncate long titles on mobile
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title || 'Plan Guide'}
        </Typography>

        {onClose && (
          <Tooltip title="Close Plan Guide (Esc)">
            <IconButton
              edge="end"
              onClick={onClose}
              sx={{
                // Ensure 44x44px touch target
                minWidth: 44,
                minHeight: 44,
              }}
              aria-label="Close Plan Guide"
              aria-keyshortcuts="Escape"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>

      {/* Progress indicator */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ height: 4 }}
        aria-label={progressLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
        aria-valuetext={progressLabel}
      />
    </AppBar>
  );
};
